var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { useDataProvider } from 'react-admin';
import { useQuery, useQueryClient, } from 'react-query';
import { getRootAndChildNodes } from '../util';
/**
 * Call the dataProvider.getTree() method and return the resolved value
 * as well as the loading state.
 *
 * The return value updates according to the request state:
 *
 * - start: { isLoading: true, refetch }
 * - success: { data: [data from store], total: [total from response], isLoading: false, refetch }
 * - error: { error: [error from response], isLoading: false, refetch }
 *
 * This hook will return the cached result when called a second time
 * with the same parameters, until the response arrives.
 *
 * @param {string} resource The resource name, e.g. 'posts'
 * @param {Params} params The getTree parameters { meta }
 * @param {Object} options Options object to pass to the queryClient.
 * May include side effects to be executed upon success or failure, e.g. { onSuccess: () => { refresh(); } }
 *
 * @typedef Params
 * @prop params.meta Optional meta parameters
 *
 * @returns The current request state. Destructure as { data, error, isLoading, isFetching }.
 *
 * The data structure is a flat array of TreeRecords (i.e. Records with a children field containing an array of Identifiers).
 *
 * @example // sample { data }
 * [
 *   { id: 1, title: 'foo1', children: [3, 4] },
 *   { id: 2, title: 'foo2', children: [] },
 *   { id: 3, title: 'foo3', children: [5] },
 *   { id: 4, title: 'foo4', children: [] },
 *   { id: 5, title: 'foo5', children: [] },
 * ]
 *
 * @example // usage in a React component
 *
 * import { useGetTree } from '@react-admin/ra-tree';
 *
 * const Categories = () => {
 *     const { data: tree, isLoading, error } = useGetTree('categories');
 *     if (isLoading) { return <Loading />; }
 *     if (error) { return <p>ERROR</p>; }
 *     return <Tree tree={data} />;
 * };
 */
export var useGetTree = function (resource, params, options) {
    if (params === void 0) { params = {}; }
    var dataProvider = useDataProvider();
    var queryClient = useQueryClient();
    var result = useQuery([resource, 'getTree', params], function () {
        return dataProvider
            .getTree(resource, params)
            .then(function (_a) {
            var data = _a.data;
            return data;
        });
    }, __assign({ onSuccess: function (data) {
            updateQueryCacheFromGetTree(queryClient, resource, data, params);
        } }, options));
    return result;
};
export var updateQueryCacheFromGetTree = function (queryClient, resource, data, params) {
    if (!data) {
        return { rootNodes: [], childNodes: [] };
    }
    var _a = getRootAndChildNodes(data), rootNodes = _a.rootNodes, childNodes = _a.childNodes;
    // optimistically populate the getRootNodes cache
    queryClient.setQueryData([resource, 'getRootNodes', params], function (oldRecords) { return oldRecords !== null && oldRecords !== void 0 ? oldRecords : rootNodes; });
    // optimistically populate the getParentNode cache
    childNodes.forEach(function (record) {
        queryClient.setQueryData([resource, 'getParentNode', { childId: record.id }], function (oldRecord) {
            return oldRecord !== null && oldRecord !== void 0 ? oldRecord : data.find(function (parent) {
                return parent.children && parent.children.includes(record.id);
            });
        });
    });
    // optimistically populate the getChildNodes cache
    data.forEach(function (record) {
        if (record.children) {
            queryClient.setQueryData([resource, 'getChildNodes', { parentId: record.id }], function (oldRecords) {
                return oldRecords !== null && oldRecords !== void 0 ? oldRecords : data.filter(function (child) { return record.children.includes(child.id); });
            });
        }
    });
};
